// ===========================================
// Students Components - Helper Functions
// ===========================================

import { FileVisibility } from '../../../api/resources.api';
import { DAY_NAMES } from './students.constants';
import type { LessonOption } from './students.types';

/**
 * Get file visibility label for display
 */
export function getFileVisibilityLabel(visibility: FileVisibility): string {
  switch (visibility) {
    case 'ALL':
      return 'Everyone';
    case 'TEACHERS_AND_PARENTS':
      return 'Teachers & Parents';
    case 'TEACHERS_ONLY':
      return 'Teachers Only';
    default:
      return visibility;
  }
}

/**
 * Get file visibility color for chip
 */
export function getFileVisibilityColor(
  visibility: FileVisibility
): 'success' | 'warning' | 'error' {
  switch (visibility) {
    case 'ALL':
      return 'success';
    case 'TEACHERS_AND_PARENTS':
      return 'warning';
    case 'TEACHERS_ONLY':
      return 'error';
    default:
      return 'warning';
  }
}

/**
 * Format day of week number to name
 */
export function formatDayOfWeek(day: number | null | undefined): string {
  if (day == null) return 'TBD';
  return DAY_NAMES[day] || 'Unknown';
}

/**
 * Format time string (HH:MM) to 12-hour format
 */
export function formatTime(time: string | null | undefined): string {
  if (!time) return 'TBD';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return 'TBD';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

/**
 * Build lesson option label
 */
export function buildLessonLabel(
  name: string,
  dayOfWeek: number,
  startTime: string
): string {
  return `${name} - ${formatDayOfWeek(dayOfWeek)} ${formatTime(startTime)}`;
}

/**
 * Filter available lessons (not already enrolled)
 */
export function getAvailableLessons(
  lessons: Array<{
    id: string;
    name: string;
    isActive: boolean;
    dayOfWeek: number | null;
    startTime: string | null;
    startWeek?: number | null;
    endWeek?: number | null;
    Term?: { name: string; startDate: string; endDate: string } | null;
  }> | undefined,
  enrolledLessonIds: Set<string>
): LessonOption[] {
  if (!lessons) return [];

  return lessons
    .filter((lesson) => lesson.isActive && !enrolledLessonIds.has(lesson.id))
    .map((lesson) => ({
      id: lesson.id,
      name: lesson.name,
      startWeek: lesson.startWeek ?? null,
      endWeek: lesson.endWeek ?? null,
      dayOfWeek: lesson.dayOfWeek,
      term: lesson.Term ?? null,
      label: lesson.dayOfWeek != null && lesson.startTime
        ? buildLessonLabel(lesson.name, lesson.dayOfWeek, lesson.startTime)
        : `${lesson.name} - Schedule TBD`,
    }));
}

/**
 * Get student status color for chip display
 */
export function getStudentStatusColor(
  status: 'NEW' | 'WAITLISTED' | 'ACTIVE' | 'INACTIVE'
): 'default' | 'primary' | 'success' | 'error' | 'warning' {
  switch (status) {
    case 'NEW':
      return 'default';
    case 'WAITLISTED':
      return 'warning';
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'error';
    default:
      return 'default';
  }
}

/**
 * Get student status label for display
 */
export function getStudentStatusLabel(status: 'NEW' | 'WAITLISTED' | 'ACTIVE' | 'INACTIVE'): string {
  switch (status) {
    case 'NEW':
      return 'New Student';
    case 'WAITLISTED':
      return 'Waitlisted';
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Inactive';
    default:
      return status;
  }
}
